import React from 'react';
import {
    IMatrixCell,
    IMatrixField,
    IMatrixParticipant,
    IMatrixPosition,
    MatrixPriceMode,
} from '../WinnerSelection2.types';
import type { PositionCriterionHandler } from '../WinnerSelection2.types';
import { cellKeyOf, formatMatrixMessage, formatQuantity, IMatrixIndex } from '../utils/matrixView';
import OfferCell, { CellIntent } from './OfferCell';
import MatrixWarning from './MatrixWarning';

interface IProps {
    position: IMatrixPosition;
    rowId: string;
    index: IMatrixIndex;
    participants: IMatrixParticipant[];
    participantIds: string[];
    showComplianceComment?: boolean;
    priceMode: MatrixPriceMode;
    priceVisible: boolean;
    metric: IMatrixField | null;
    nextMetricTitle: string | null;
    onNextMetric: () => void;
    busy: boolean;
    onPositionCriterion: PositionCriterionHandler;
    onCellIntent: (intent: CellIntent, cell: IMatrixCell, trigger: HTMLElement) => void;
}

/** Позиция: потребность и выбранные объёмы на оси, предложения участников в ячейках. */
const PositionRow: React.FC<IProps> = ({
    position,
    rowId,
    index,
    participants,
    participantIds,
    showComplianceComment,
    priceMode,
    priceVisible,
    metric,
    nextMetricTitle,
    onNextMetric,
    busy,
    onPositionCriterion,
    onCellIntent,
}) => {
    const unit = position.unit ?? '';
    const totals: [string, number | null][] = [
        ['Требуется', position.requiredQuantity],
        ['Основной', position.mainQuantity],
        ['Резервный', position.reserveQuantity],
    ];
    return (
        <tr className="ws2-position">
            <th scope="row" id={rowId}>
                <div className="ws2-position__name">
                    <span className="ws2-position__number">{index.positionNumbers.get(position.key)}. </span>
                    {position.name}
                </div>
                {position.description && (
                    <div className="ws2-position__description" title={position.description}>
                        {position.description}
                    </div>
                )}
                <div className="ws2-position__quantities">
                    {totals.map(([label, value], i) => (
                        <div
                            className={`ws2-position__row${i === 0 ? ' ws2-position__row--required' : ''}`}
                            key={label}
                        >
                            <span>{label}</span>
                            <strong className={i > 0 && value === 0 ? 'ws2-position__quantity--zero' : undefined}>
                                {formatQuantity(value)}
                                {unit && (
                                    <>
                                        {' '}
                                        <span className="ws2-position__unit">{unit}</span>
                                    </>
                                )}
                            </strong>
                        </div>
                    ))}
                </div>
                {position.warnings.map((warning) => (
                    <MatrixWarning className="ws2-position__warning" key={warning}>
                        {formatMatrixMessage(warning)}
                    </MatrixWarning>
                ))}
            </th>
            {participants.map((participant, i) => (
                <OfferCell
                    key={participant.key}
                    cell={index.cells.get(cellKeyOf(position.key, participant.key))}
                    position={position}
                    participant={participant}
                    headers={`${rowId} ${participantIds[i]}`}
                    showComplianceComment={showComplianceComment}
                    priceMode={priceMode}
                    priceVisible={priceVisible}
                    metric={metric}
                    nextMetricTitle={nextMetricTitle}
                    onNextMetric={onNextMetric}
                    onPositionCriterion={onPositionCriterion}
                    busy={busy}
                    onIntent={onCellIntent}
                />
            ))}
        </tr>
    );
};

export default PositionRow;
